import DOMPurify from "dompurify";

/**
 * Service de sanitisation HTML pour sécuriser les contenus affichés avec v-html
 * Utilise DOMPurify pour nettoyer le HTML tout en préservant le formatage
 */
export default {
  /**
   * Sanitise le contenu des articles (contenu riche éditorial)
   * Autorise un large éventail de balises pour le formatage riche
   * @param {string} html - Contenu HTML à sanitiser
   * @returns {string} - HTML sanitisé
   */
  sanitizeArticleContent(html) {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        // Texte et formatage
        "p",
        "br",
        "hr",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "s",
        "strike",
        "del",
        "ins",
        "mark",
        "small",
        "sub",
        "sup",
        // Titres
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        // Listes
        "ul",
        "ol",
        "li",
        "dl",
        "dt",
        "dd",
        // Liens et médias
        "a",
        "img",
        "figure",
        "figcaption",
        // Citations et code
        "blockquote",
        "cite",
        "q",
        "code",
        "pre",
        "kbd",
        "samp",
        "var",
        // Tableaux
        "table",
        "thead",
        "tbody",
        "tfoot",
        "tr",
        "th",
        "td",
        "caption",
        "colgroup",
        "col",
        // Structure
        "div",
        "span",
        "section",
        "article",
        "aside",
        "header",
        "footer",
        "main",
        "nav",
        // Formulaires
        "button",
        "label",
        // Styles
        "style",
        // Autres
        "abbr",
        "acronym",
        "address",
        "time",
      ],
      ALLOWED_ATTR: [
        "href",
        "src",
        "alt",
        "title",
        "class",
        "id",
        "style",
        "width",
        "height",
        "target",
        "rel",
        "datetime",
        "cite",
        "lang",
        "dir",
        "colspan",
        "rowspan",
        "scope",
        "headers",
        "abbr",
        "axis",
        "type",
        "onclick",
        "data-product-id",
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      ALLOW_DATA_ATTR: false,
      FORBID_ATTR: ["onerror", "onload", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
      FORBID_TAGS: ["script", "object", "embed", "form", "input", "textarea", "select", "option"],
    });
  },

  /**
   * Sanitise les descriptions produits (formatage riche mais contrôlé)
   * @param {string} html - Contenu HTML à sanitiser
   * @returns {string} - HTML sanitisé
   */
  sanitizeProductInfo(html) {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "hr",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "mark",
        "small",
        "font",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "dl",
        "dt",
        "dd",
        "div",
        "span",
        "section",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "caption",
        "blockquote",
        "code",
        "pre",
      ],
      ALLOWED_ATTR: ["class", "id", "style", "title", "colspan", "rowspan", "scope", "headers", "color"],
      ALLOW_DATA_ATTR: false,
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
    });
  },

  /**
   * Sanitise le contenu carousel/hero (très riche pour les bannières)
   * @param {string} html - Contenu HTML à sanitiser
   * @returns {string} - HTML sanitisé
   */
  sanitizeCarouselContent(html) {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "hr",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "mark",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "a",
        "img",
        "figure",
        "figcaption",
        "div",
        "span",
        "section",
        "article",
        "blockquote",
        "cite",
      ],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "id", "style", "width", "height", "target", "rel"],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      ALLOW_DATA_ATTR: false,
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
    });
  },

  /**
   * Sanitise le contenu i18n (formatage simple mais sûr)
   * @param {string} html - Contenu HTML à sanitiser
   * @returns {string} - HTML sanitisé
   */
  sanitizeI18nContent(html) {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["span", "strong", "b", "em", "i", "u", "br", "a", "small", "mark"],
      ALLOWED_ATTR: ["href", "class", "target", "rel", "title"],
      ALLOW_DATA_ATTR: false,
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
    });
  },

  /**
   * Sanitise les contenus SVG (sécurisé mais fonctionnel)
   * @param {string} html - Contenu SVG à sanitiser
   * @returns {string} - SVG sanitisé
   */
  sanitizeSvgContent(html) {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      USE_PROFILES: { svg: true, svgFilters: false },
      ALLOWED_TAGS: [
        "svg",
        "g",
        "path",
        "circle",
        "rect",
        "line",
        "polyline",
        "polygon",
        "ellipse",
        "text",
        "tspan",
        "defs",
        "use",
        "symbol",
        "marker",
      ],
      ALLOWED_ATTR: [
        "viewBox",
        "width",
        "height",
        "fill",
        "stroke",
        "stroke-width",
        "stroke-linecap",
        "stroke-linejoin",
        "opacity",
        "transform",
        "d",
        "cx",
        "cy",
        "r",
        "rx",
        "ry",
        "x",
        "y",
        "x1",
        "y1",
        "x2",
        "y2",
        "points",
        "class",
        "id",
      ],
      ALLOW_DATA_ATTR: false,
    });
  },

  /**
   * Sanitise les descriptions de modes de livraison
   * @param {string} html - Contenu HTML à sanitiser
   * @returns {string} - HTML sanitisé
   */
  sanitizeDeliveryContent(html) {
    if (!html) return "";

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "span", "div", "ul", "ol", "li", "small"],
      ALLOWED_ATTR: ["class", "style"],
      ALLOW_DATA_ATTR: false,
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
    });
  },

  /**
   * Méthode personnalisable pour cas spéciaux
   * @param {string} html - Contenu HTML à sanitiser
   * @param {Object} options - Options personnalisées pour DOMPurify
   * @returns {string} - HTML sanitisé
   */
  customSanitize(html, options = {}) {
    if (!html) return "";

    const defaultOptions = {
      ALLOWED_TAGS: ["p", "br", "strong", "em", "span"],
      ALLOWED_ATTR: ["class"],
      ALLOW_DATA_ATTR: false,
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
    };

    return DOMPurify.sanitize(html, { ...defaultOptions, ...options });
  },

  /**
   * Utilitaire pour vérifier si le contenu est déjà sûr
   * @param {string} html - Contenu HTML à vérifier
   * @returns {boolean} - True si le contenu est sûr
   */
  isContentSafe(html) {
    if (!html) return true;
    const cleaned = DOMPurify.sanitize(html);
    return cleaned === html;
  },

  /**
   * Utilitaire pour obtenir uniquement le texte sans HTML
   * @param {string} html - Contenu HTML
   * @returns {string} - Texte brut
   */
  getTextOnly(html) {
    if (!html) return "";
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  },
};
